import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { StatsSection } from 'widgets/statspro/js/components/common/stats-section';

import {
    CompNavItem,
    ResponsiveRoundNavItem,
    ResponsiveSeasonNavItem,
    RoundSelectNavItem
} from 'common/react/components/competition-nav/nav-items';
import { navSelector } from 'common/store/modules/competition-nav/selectors';
import { useCompetitionNavigationOptions } from 'common/react/hooks/use-competition-navigation-options';
import { useNavSync } from 'common/react/components/competition-nav/hooks/use-nav-sync';
import PropTypes from 'prop-types';
import { ALLOWED_COMPS } from 'widgets/ladder-v2/js/components/LadderNav';
import { FilterDropdown } from 'widgets/filters/js/components/FilterDropdown';
import { UPDATE_NAV_ITEM } from 'common/store/modules/competition-nav/actions';
import { getTranslation } from 'common/react/utils/translations';
import { FilterButton } from 'common/react/components/FilterButton';
import { useWindowWidth } from 'common/react/hooks/use-window-resize';
import { StatsButton } from 'common/react/components/StatsButton';

const TIMEOUT = 500;

export const CustomLadderNav = ({ seasonId }) => {
    const dispatch = useDispatch();

    const initialValues = {
        seasonId: seasonId
    };

    const nav = useSelector(navSelector);
    const options = useCompetitionNavigationOptions(nav);
    const competitionNavRef = useRef();
    const windowWidth = useWindowWidth();
    const isResponsive = () =>
        typeof PULSE?.app?.measurements?.desktop === 'number' &&
        windowWidth <= PULSE.app.measurements.desktop;
    const [showResponsiveItems, setShowResponsiveItems] = useState(false);

    const [areFilterOptionsVisible, setAreFilterOptionsVisible] =
        useState(false);
    const [resetFilterBtnStyles, setResetFilterBtnStyles] = useState(false);

    useEffect(() => {
        setShowResponsiveItems(isResponsive());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [windowWidth]);

    const compseason = nav?.season.id;
    const competition = nav?.competition.id;
    const roundId = nav?.round.id;

    let timeoutId = null;

    useNavSync({ ...options, initialValues, removeFinals: true });
    const competitions = options.competitions;

    const seasons = useSelector(
        (state) =>
            state.seasons[`competition_${nav.competition.id}`]?.list ?? []
    );

    const season = seasons.find((item) => item.id === nav.season.id);

    useEffect(() => {
        // clear prev timeout
        if (timeoutId) {
            clearTimeout(timeoutId);
        }

        timeoutId = setTimeout(() => {
            dispatchLadderInit();
        }, TIMEOUT);

        return () => {
            clearTimeout(timeoutId);
        };
    }, [competition, compseason, roundId]);

    const dispatchLadderInit = () => {
        const params = {
            roundId: nav.round.id
        };

        const string = Object.keys(params)
            .map((key) => {
                return (
                    encodeURIComponent(key) +
                    '=' +
                    encodeURIComponent(params[key])
                );
            })
            .join('&');

        const currentRoundNumber =
            season?.currentRoundNumber === 0 ? 1 : season?.currentRoundNumber;

        const isFutureRound = nav.round.roundNumber > currentRoundNumber;

        const endpoint = `${PULSE.app.environment.api}compseasons/${
            nav.season.id
        }/ladders${!isFutureRound ? '?' + string : ''}`;

        dispatch(
            PULSE.app.redux.actions.customtable.RE_INIT_TABLE.request({
                tableName: 'ladder',
                apiUrl: endpoint,
                retryEndPoint: false
            })
        );
    };

    const hasRoundChanged = useCallback(() => {
        if (!nav?.round) {
            return false;
        }

        return nav?.round?.id !== nav?.round?.initialItemId;
    }, [nav?.round]);

    const isSeasonChanged = useCallback(() => {
        if (!nav?.season) {
            return false;
        }

        return (
            parseInt(nav?.season?.id, 10) !==
            parseInt(nav?.season?.initialItemId, 10)
        );
    }, [nav?.season]);

    /**
     * Checks if any navigation filters have changed from their initial default values.
     *
     * @returns {boolean} True if any filter has changed from defaults
     */
    const hasChangedFromDefaults = () => {
        if (!nav?.competition || !nav?.season || !nav?.round) {
            return false;
        }
        const isCompetitionChanged =
            nav?.competition?.id !== nav?.competition?.initialItemId;

        return isCompetitionChanged || isSeasonChanged() || hasRoundChanged();
    };

    /**
     * Resets all navigation filters (competition, season, round) back to their initial default values.
     */
    const resetToDefaults = () => {
        const initialCompetitionId = nav?.competition?.initialItemId || '';
        const initialSeasonId = nav?.season?.initialItemId || '';
        const initialRoundId = nav?.round?.initialItemId || '';

        if (nav?.competition?.id !== initialCompetitionId) {
            dispatch(
                UPDATE_NAV_ITEM.request({
                    navItem: 'competition',
                    value: { id: initialCompetitionId }
                })
            );
        }
        if (nav?.season?.id !== initialSeasonId) {
            dispatch(
                UPDATE_NAV_ITEM.request({
                    navItem: 'season',
                    value: { id: initialSeasonId }
                })
            );
        }
        if (nav?.round?.id !== initialRoundId) {
            dispatch(
                UPDATE_NAV_ITEM.request({
                    navItem: 'round',
                    value: { id: initialRoundId }
                })
            );
        }
    };

    const renderMobile = () => {
        return (
            <>
                <StatsSection.NavItems>
                    <CompNavItem
                        competitions={competitions}
                        allowListOrder={ALLOWED_COMPS}
                        hideLabel={true}
                        classes="fixtures-nav__comp-select"
                    />
                    <FilterButton
                        classes="fixtures-nav__filter-button"
                        onChange={() =>
                            setAreFilterOptionsVisible((prev) => !prev)
                        }
                        areFilterOptionsVisible={areFilterOptionsVisible}
                        resetFilterBtnStyles={resetFilterBtnStyles}
                        setResetFilterBtnStyles={setResetFilterBtnStyles}
                        showFilterLabel={false}
                        showActiveCircle
                        isFilterSelectedCustom={() =>
                            isSeasonChanged() || hasRoundChanged()
                        }
                    />
                </StatsSection.NavItems>
                {areFilterOptionsVisible && (
                    <StatsSection.NavFilterItems hideReset>
                        <ResponsiveSeasonNavItem
                            seasons={seasons}
                            type="with-default"
                        />
                        <ResponsiveRoundNavItem
                            removeFinals
                            selector={navSelector}
                            hideLabel
                        />
                        {hasChangedFromDefaults() ? (
                            <div className="stats-leaders-nav__item-wrapper stats-leaders-nav__item-wrapper--reset">
                                <button
                                    className="stats-leaders-filter-reset-button"
                                    onClick={resetToDefaults}
                                >
                                    {getTranslation(
                                        'label.statsLeaders.filters.resetButtonText'
                                    )}
                                </button>
                            </div>
                        ) : null}
                    </StatsSection.NavFilterItems>
                )}
                <StatsSection.SettingsButton classes="fixtures-nav__settings-button">
                    <StatsButton />
                </StatsSection.SettingsButton>
            </>
        );
    };

    const renderDesktop = () => {
        return (
            <>
                <StatsSection.NavItems>
                    <CompNavItem
                        competitions={competitions}
                        allowListOrder={ALLOWED_COMPS}
                        hideLabel={false}
                    />
                    {!showResponsiveItems ? (
                        <FilterDropdown
                            title="Season"
                            icon="season-trophy"
                            navItem="season"
                            items={seasons}
                            onChange={() =>
                                setAreFilterOptionsVisible((prev) => !prev)
                            }
                        />
                    ) : null}

                    {!showResponsiveItems ? (
                        <RoundSelectNavItem removeFinals={true} />
                    ) : null}
                    {hasChangedFromDefaults() ? (
                        <div className="stats-leaders-nav__item-wrapper stats-leaders-nav__item-wrapper--reset">
                            <button
                                className="stats-leaders-filter-reset-button"
                                onClick={resetToDefaults}
                            >
                                {getTranslation(
                                    'label.statsLeaders.filters.resetButtonText'
                                )}
                            </button>
                        </div>
                    ) : null}
                </StatsSection.NavItems>

                {showResponsiveItems ? (
                    <StatsSection.NavFilterItems hideReset={true}>
                        <ResponsiveSeasonNavItem seasons={seasons} />
                        <ResponsiveRoundNavItem
                            removeFinals={true}
                            selector={navSelector}
                            hideLabel={true}
                        />
                    </StatsSection.NavFilterItems>
                ) : null}

                <StatsSection.SettingsButton>
                    <StatsButton />
                </StatsSection.SettingsButton>
            </>
        );
    };

    return (
        <div
            className="competition-nav filter-dropdown rounded-filter-wrapper"
            ref={competitionNavRef}
        >
            <StatsSection.Nav
                showResponsiveItems={showResponsiveItems}
                showFilterItems={areFilterOptionsVisible}
                withScrollableFilter={!showResponsiveItems}
            >
                {showResponsiveItems ? renderMobile() : renderDesktop()}
            </StatsSection.Nav>
        </div>
    );
};

CustomLadderNav.propTypes = {
    seasonId: PropTypes.string
};
